import React, { useEffect, useState, useMemo } from "react"
import { supabase } from "@/integrations/supabase/client"
import { Card, CardContent } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Checkbox } from "@/components/ui/checkbox"
import { Download, Trash2, Globe, AlertCircle, Sparkles, CheckSquare, Volume2, RefreshCcw } from "lucide-react"
import { useToast } from "@/hooks/use-toast"
import { ShareToggle } from "./ShareToggle"
import { ContentLikeButton } from "./ContentLikeButton"
import { OptimizedLikeButton } from "./OptimizedLikeButton"
import { useBatchLikeCounts } from "@/hooks/useBatchLikeCounts"
import { VIDEO_MODELS } from "@/config/videoModels"
import { MediaLightbox } from "./MediaLightbox"
import { GallerySelectionBar } from "./GallerySelectionBar"
import { ClickablePrompt } from "./ClickablePrompt"
import { useGallerySelection } from "@/hooks/useGallerySelection"
import { useVideoRef } from "@/hooks/useVideoVisibility"
import { DeleteConfirmDialog } from "./DeleteConfirmDialog"

// Video player component with visibility-based playback
interface GalleryVideoPlayerProps {
  src: string;
  onClick?: () => void;
  className?: string;
}

function GalleryVideoPlayer({ src, onClick, className }: GalleryVideoPlayerProps) {
  const videoRef = useVideoRef();
  
  return (
    <video
      ref={videoRef}
      src={src}
      className={className}
      muted
      loop
      playsInline
      preload="metadata"
      crossOrigin="anonymous"
      onClick={onClick}
      onError={(e) => {
        // Use warn instead of error to avoid Lighthouse flagging as browser error
        e.currentTarget.style.display = 'none';
        const img = document.createElement('img');
        img.src = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='320' height='180' viewBox='0 0 320 180'%3E%3Crect width='320' height='180' fill='%23f3f4f6'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' fill='%236b7280' font-family='system-ui' font-size='14'%3EVideo Loading...%3C/text%3E%3C/svg%3E";
        img.className = 'w-full h-full object-cover';
        img.alt = 'Video loading placeholder';
        e.currentTarget.parentNode?.appendChild(img);
      }}
    />
  );
}

interface Video {
  id: string
  prompt: string
  status: string
  downloads?: any
  created_at: string
  name?: string
  user_id?: string
  storage_status?: string
  storage_attempts?: number
  storage_error?: string
  storage_completed_at?: string
  is_public?: boolean
  error_message?: string | null
  credits_used?: number
  model?: string
  audio_url?: string | null
  creator_username?: string | null
}

interface VideoGalleryProps {
  refreshTrigger?: number
  generatingVideos?: Set<string>
  onModelClick?: (model: string) => void
  onRetry?: (prompt: string, settings: any) => void
}

export function VideoGallery({ refreshTrigger, generatingVideos = new Set(), onModelClick, onRetry }: VideoGalleryProps) {
  const [videos, setVideos] = useState<Video[]>([])
  const [loading, setLoading] = useState(true)
  const [user, setUser] = useState<any>(null)
  const [isInitialLoad, setIsInitialLoad] = useState(true)
  const [currentPage, setCurrentPage] = useState(0)
  const [hasMore, setHasMore] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [lightboxVideo, setLightboxVideo] = useState<Video | null>(null)
  const { toast } = useToast()

  // Batch like counts for public videos (reduces N queries to 2)
  const publicVideosForLikes = useMemo(() => 
    !user ? videos.filter(v => v.is_public && v.status === 'completed').map(v => ({ type: 'video' as const, id: v.id })) : [],
    [videos, user]
  )
  const { getLikeData, updateLikeData, userId: batchUserId } = useBatchLikeCounts(publicVideosForLikes)

  useEffect(() => {
    checkUser()
    
    // Set up auth state listener to refresh videos when auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setUser(session?.user ?? null)
      }
    )

    return () => subscription.unsubscribe()
  }, [])

  useEffect(() => {
    fetchVideos()
  }, [user, refreshTrigger])

  // Set up targeted realtime updates for video changes
  useEffect(() => {
    if (!user) return;

    const channel = supabase
      .channel(`video-updates-${user.id}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'generated_videos',
          filter: `user_id=eq.${user.id}`
        },
        (payload) => {
          console.log('Video update received:', payload)
          
          // Update specific video in state instead of refetching all
          if (payload.eventType === 'UPDATE' && payload.new) {
            setVideos(prev => prev.map(video => 
              video.id === payload.new.id ? { ...video, ...payload.new } : video
            ));
          } else if (payload.eventType === 'INSERT' && payload.new) {
            setVideos(prev => [payload.new as Video, ...prev]);
          } else if (payload.eventType === 'DELETE' && payload.old) {
            setVideos(prev => prev.filter(video => video.id !== payload.old.id));
          }
        }
      )
      .subscribe()

    return () => {
      supabase.removeChannel(channel)
    }
  }, [user])

  const checkUser = async () => {
    const { data: { user } } = await supabase.auth.getUser()
    setUser(user)
  }

  const fetchVideos = async (reset = true) => {
    // Only show loading on initial load, not on refreshes
    if (isInitialLoad && reset) {
      setLoading(true)
    }
    
    try {
      const pageSize = 12
      const startIndex = reset ? 0 : videos.length
      
      if (!user) {
        // Unauthenticated users see public videos via secure view (no user_id exposed)
        const { data, error } = await supabase
          .from('public_videos')
          .select('*')
          .order('created_at', { ascending: false })
          .range(startIndex, startIndex + pageSize - 1)

        if (error) {
          console.error('Error fetching public videos:', error)
          return
        }

        const newVideos = (data || []).map(v => ({ ...v, is_public: true })) as Video[]
        
        if (reset) {
          setVideos(newVideos)
          setCurrentPage(0)
        } else {
          setVideos(prev => [...prev, ...newVideos])
          setCurrentPage(prev => prev + 1)
        }
        
        setHasMore(newVideos.length === pageSize)
      } else {
        // Authenticated users: RLS filters to their own videos
        const { data, error } = await supabase
          .from('generated_videos')
          .select('*')
          .order('created_at', { ascending: false })
          .range(startIndex, startIndex + pageSize - 1)

        if (error) {
          console.error('Error fetching videos:', error)
          if (isInitialLoad && reset) {
            toast({
              title: "Error",
              description: "Failed to load videos",
              variant: "destructive"
            })
          }
          return
        }

        const newVideos = data || []
        
        if (reset) {
          setVideos(newVideos)
          setCurrentPage(0)
        } else {
          setVideos(prev => [...prev, ...newVideos])
          setCurrentPage(prev => prev + 1)
        }
        
        setHasMore(newVideos.length === pageSize)
      }
    } catch (error) {
      console.error('Error:', error)
    } finally {
      if (isInitialLoad && reset) {
        setLoading(false)
        setIsInitialLoad(false)
      }
      if (!reset) {
        setLoadingMore(false)
      }
    }
  }

  const loadMoreVideos = async () => {
    if (loadingMore || !hasMore) return
    
    setLoadingMore(true)
    await fetchVideos(false)
  }

  const handleDownload = async (video: Video) => {
    if (!video.downloads?.video_url) {
      toast({
        title: "Error",
        description: "Video URL not available",
        variant: "destructive"
      })
      return
    }

    try {
      toast({
        title: "Downloading...",
        description: "Please wait while the video is being prepared"
      })

      const response = await fetch(video.downloads.video_url)
      if (!response.ok) throw new Error('Failed to fetch video')

      const blob = await response.blob()
      const blobUrl = window.URL.createObjectURL(blob)

      const link = document.createElement('a')
      link.style.display = 'none'
      link.href = blobUrl
      link.download = `${(video.name || video.prompt || 'video').slice(0, 30).replace(/[^a-z0-9]/gi, '_').toLowerCase()}.mp4`
      document.body.appendChild(link)
      link.click()

      setTimeout(() => {
        document.body.removeChild(link)
        window.URL.revokeObjectURL(blobUrl)
      }, 100)

      toast({
        title: "Success",
        description: "Video download started"
      })
    } catch (error) {
      console.error('Download error:', error)
      // Fallback: open in new tab for iOS Safari
      window.open(video.downloads.video_url, '_blank')
      toast({
        title: "Download",
        description: "If download didn't start, long-press the video to save it"
      })
    }
  }

  // Single delete state
  const [deleteConfirmVideo, setDeleteConfirmVideo] = useState<Video | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const performDelete = async (video: Video) => {
    try {
      // Delete video file from storage if it exists
      if (video.downloads?.video_url && video.downloads.video_url.includes('supabase.co')) {
        const urlParts = video.downloads.video_url.split('/')
        const fileName = urlParts[urlParts.length - 1]
        await supabase.storage.from('videos').remove([fileName])
      }

      // Delete video record from database
      const { error } = await supabase
        .from('generated_videos')
        .delete()
        .eq('id', video.id)

      if (error) {
        console.error('Error deleting video:', error)
        toast({
          title: "Error",
          description: "Failed to delete video",
          variant: "destructive"
        })
        throw error
      }

      // Remove from local state
      setVideos(videos.filter(v => v.id !== video.id))

      toast({
        title: "Success",
        description: video.status === 'processing' 
          ? "Processing video cancelled and deleted"
          : "Video deleted successfully"
      })
    } catch (error) {
      console.error('Error:', error)
      throw error
    }
  }

  const handleDelete = async (video: Video) => {
    setDeleteConfirmVideo(video);
  }

  const handleRefreshStatus = async (video: Video) => {
    toast({
      title: "Checking status...",
      description: "Querying the generation service"
    });

    try {
      const { data, error } = await supabase.functions.invoke('fix-stuck-videos', {
        body: {}
      });

      if (error) throw error;

      toast({
        title: "Status checked",
        description: "Video status has been updated"
      });
    } catch (error) {
      console.error('Error refreshing status:', error);
      toast({
        title: "Error",
        description: "Failed to check status. Try again later.",
        variant: "destructive"
      });
    }
  }

  const confirmSingleDelete = async () => {
    if (!deleteConfirmVideo) return;
    setIsDeleting(true);
    try {
      await performDelete(deleteConfirmVideo);
    } finally {
      setIsDeleting(false);
      setDeleteConfirmVideo(null);
    }
  }

  const handleDeleteById = async (id: string) => {
    const video = videos.find(v => v.id === id);
    if (video) await performDelete(video);
  }

  // Gallery selection hook
  const {
    selectedItems,
    selectionMode,
    isBulkDeleting,
    isBulkDownloading,
    toggleSelection,
    selectAll,
    deselectAll,
    cancelSelection,
    startSelection,
    bulkDelete,
    bulkDownload,
    showBulkDeleteConfirm,
    setShowBulkDeleteConfirm,
    confirmBulkDelete,
    selectedCount,
  } = useGallerySelection({
    items: videos.filter(v => v.status === 'completed' && v.downloads?.video_url),
    type: 'video',
    onDelete: handleDeleteById,
    onRefresh: () => setVideos(prev => [...prev]),
  });

  const getVideoThumbnail = (video: Video) => {
    console.log('Video data:', { id: video.id, status: video.status, downloads: video.downloads })
    // If video is completed and has a video URL, return the video URL for preview
    if (video.status === 'completed' && video.downloads?.video_url) {
      console.log('Returning video URL:', video.downloads.video_url)
      return video.downloads.video_url
    }
    // For processing or failed videos, return a placeholder
    console.log('Returning placeholder for video:', video.id)
    return "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='320' height='180' viewBox='0 0 320 180'%3E%3Crect width='320' height='180' fill='%23f3f4f6'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' fill='%236b7280' font-family='system-ui' font-size='14'%3EVideo Thumbnail%3C/text%3E%3C/svg%3E"
  }

  const isVideoUrl = (url: string) => {
    return url.includes('http') && !url.includes('data:image/svg')
  }

  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {Array.from({ length: 6 }).map((_, i) => (
          <Card key={i} className="overflow-hidden animate-pulse">
            <div className="aspect-video bg-gray-200"></div>
            <CardContent className="p-4">
              <div className="h-4 bg-gray-200 rounded mb-2"></div>
              <div className="h-3 bg-gray-200 rounded w-2/3"></div>
            </CardContent>
          </Card>
        ))}
      </div>
    )
  }

  if (videos.length === 0) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">
          {user ? "No videos generated yet. Create your first video!" : "No public videos available yet."}
        </p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Selection header */}
      {user && !selectionMode && videos.some(v => v.status === 'completed' && v.downloads?.video_url) && (
        <div className="flex justify-end">
          <Button variant="outline" size="sm" onClick={startSelection}>
            <CheckSquare className="h-4 w-4 mr-2" />
            Select
          </Button>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {videos.map((video) => {
          const isGenerating = generatingVideos.has(video.id);
          return (
            <Card key={video.id} className={`group overflow-hidden hover:shadow-lg transition-shadow relative ${selectionMode && selectedItems.has(video.id) ? 'ring-2 ring-primary' : ''}`}>
               {/* Selection checkbox */}
               {selectionMode && video.status === 'completed' && video.downloads?.video_url && (
                 <div 
                   className="absolute top-2 left-2 z-30"
                   onClick={(e) => {
                     e.stopPropagation();
                     toggleSelection(video.id);
                   }}
                 >
                   <Checkbox
                     checked={selectedItems.has(video.id)}
                     className="h-5 w-5 bg-white/90 border-2"
                   />
                 </div>
               )}
               <div className="relative aspect-video bg-gray-100">
                  {isVideoUrl(getVideoThumbnail(video)) ? (
                    <GalleryVideoPlayer
                      src={getVideoThumbnail(video)}
                      className={`w-full h-full object-cover ${video.status === 'completed' ? 'cursor-pointer' : ''}`}
                      onClick={() => video.status === 'completed' && video.downloads?.video_url && setLightboxVideo(video)}
                    />
                  ) : (
                   <img
                     src={getVideoThumbnail(video)}
                     alt={video.prompt || 'Video thumbnail'}
                     className="w-full h-full object-cover"
                     loading="lazy"
                   />
                 )}
                 
                 {/* Hover overlay with buttons - only for completed videos */}
                 {video.status === 'completed' && video.downloads?.video_url && (
                   <div 
                     className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2 z-10"
                     onClick={() => setLightboxVideo(video)}
                   >
                     <Button
                       size="sm"
                       variant="secondary"
                       onClick={(e) => {
                         e.stopPropagation();
                         handleDownload(video);
                       }}
                     >
                       <Download className="h-4 w-4" />
                     </Button>
                     {user && video.user_id === user.id && (
                       <>
                         <div onClick={(e) => e.stopPropagation()}>
                           <ShareToggle
                             id={video.id}
                             isPublic={video.is_public || false}
                             type="video"
                             prompt={video.prompt}
                             onToggle={(newState) => {
                               setVideos(videos.map(v => 
                                 v.id === video.id ? { ...v, is_public: newState } : v
                               ))
                             }}
                           />
                         </div>
                         <Button
                           size="sm"
                           variant="destructive"
                           onClick={(e) => {
                             e.stopPropagation();
                             handleDelete(video);
                           }}
                         >
                           <Trash2 className="h-4 w-4" />
                         </Button>
                       </>
                     )}
                   </div>
                 )}

                  {video.status === 'failed' && (
                    <div className="absolute inset-0 bg-black/80 flex flex-col items-center justify-center p-4">
                      <AlertCircle className="h-10 w-10 text-destructive mb-2" />
                      <p className="text-white text-sm font-medium mb-1">Generation Failed</p>
                      {video.error_message && (
                        <p className="text-white/70 text-xs text-center line-clamp-3 max-w-[200px] mb-2">
                          {video.error_message}
                        </p>
                      )}
                      <p className="text-green-400 text-xs mb-3">Credits refunded</p>
                      {user && video.user_id === user.id && (
                        <div className="flex gap-2">
                          {onRetry && (
                            <Button
                              size="sm"
                              variant="secondary"
                              onClick={() => {
                                onRetry(video.prompt, { model: video.model });
                                handleDelete(video);
                              }}
                              className="text-white"
                            >
                              <RefreshCcw className="w-4 h-4 mr-1" />
                              Retry
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleDelete(video)}
                            className="text-white hover:bg-white/20"
                          >
                            <Trash2 className="w-4 h-4 mr-1" />
                            Remove
                          </Button>
                        </div>
                      )}
                    </div>
                  )}
                  {video.status === 'queued' && (
                    <div className="absolute inset-0 bg-black/50 flex flex-col items-center justify-center gap-4">
                      <div className="relative w-16 h-16">
                        <div className="absolute inset-0 rounded-full border-4 border-white/20"></div>
                        <div className="absolute inset-0 rounded-full border-4 border-primary border-t-transparent animate-spin" style={{animationDuration: '1.5s'}}></div>
                      </div>
                      <div className="text-white text-sm text-center">
                        <div className="font-medium">Queued</div>
                        <div className="text-xs opacity-75">Starting generation...</div>
                      </div>
                      {user && video.user_id === user.id && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleDelete(video)}
                          className="text-white hover:bg-white/20"
                        >
                          <Trash2 className="w-4 h-4 mr-1" />
                          Cancel
                        </Button>
                      )}
                    </div>
                  )}
                  {(video.status === 'processing' && !isGenerating) && (
                    <div className="absolute inset-0 bg-black/50 flex flex-col items-center justify-center gap-4">
                      <div className="relative w-16 h-16">
                        <div className="absolute inset-0 rounded-full border-4 border-white/20"></div>
                        <div className="absolute inset-0 rounded-full border-4 border-white border-t-transparent animate-spin" style={{animationDuration: '2s'}}></div>
                      </div>
                      <div className="text-white text-sm text-center">
                        <div className="font-medium">Generating...</div>
                        <div className="text-xs opacity-75">Usually takes 30 seconds</div>
                      </div>
                      {user && video.user_id === user.id && (
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="secondary"
                            onClick={() => handleRefreshStatus(video)}
                            className="text-white"
                          >
                            <RefreshCcw className="w-4 h-4 mr-1" />
                            Check Status
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleDelete(video)}
                            className="text-white hover:bg-white/20"
                          >
                            <Trash2 className="w-4 h-4 mr-1" />
                            Cancel
                          </Button>
                        </div>
                      )}
                    </div>
                  )}
                   {isGenerating && video.status !== 'completed' && video.status !== 'queued' && (
                     <div className="absolute inset-0 bg-black/50 flex flex-col items-center justify-center gap-4">
                      <div className="relative w-16 h-16">
                        <div className="absolute inset-0 rounded-full border-4 border-white/20"></div>
                        <div className="absolute inset-0 rounded-full border-4 border-white border-t-transparent animate-spin" style={{animationDuration: '2s'}}></div>
                      </div>
                      <div className="text-white text-sm text-center">
                        <div className="font-medium">Generating...</div>
                        <div className="text-xs opacity-75">Usually takes 30 seconds</div>
                      </div>
                    </div>
                  )}
              </div>
              <CardContent className="p-4">
                <ClickablePrompt 
                  text={video.prompt} 
                  tab="video"
                  className="font-medium text-sm mb-2 line-clamp-2 pr-4"
                />
                 <div className="flex items-center justify-between">
                   <div className="flex gap-1 flex-wrap">
                     {(video.status !== 'completed' || isGenerating) && (
                       <Badge
                         variant={
                           (video.status === 'processing' || video.status === 'queued' || isGenerating) ? 'secondary' :
                           'destructive'
                         }
                         className="text-xs"
                       >
                         {isGenerating ? 'generating' : video.status}
                       </Badge>
                     )}
                     {video.is_public && (
                       <Badge
                         variant="outline"
                         className="text-xs bg-green-500/20 text-green-500 border-green-500/50"
                       >
                         <Globe className="h-3 w-3 mr-1" />
                         Public
                       </Badge>
                     )}
                     {/* Model badge - clickable for public videos */}
                     {video.model && (
                       <Badge
                         variant="outline"
                         className={`text-xs bg-purple-500/20 text-purple-300 border-purple-500/50 ${onModelClick ? 'cursor-pointer hover:bg-purple-500/30 transition-colors' : ''}`}
                         onClick={() => onModelClick && onModelClick(video.model!)}
                         title={onModelClick ? "Try this model" : undefined}
                       >
                         <Sparkles className="h-3 w-3 mr-1" />
                         {VIDEO_MODELS[video.model]?.displayName || video.model}
                       </Badge>
                     )}
                     {video.audio_url && (
                       <Badge
                         variant="outline"
                         className="text-xs bg-orange-400/20 text-orange-400 border-orange-400/50"
                       >
                         <Volume2 className="h-3 w-3" />
                       </Badge>
                     )}
                   </div>
                  {video.status === 'completed' && video.is_public && (
                    user ? (
                      <ContentLikeButton
                        contentType="video"
                        contentId={video.id}
                        className="h-8"
                      />
                    ) : (
                      <OptimizedLikeButton
                        contentType="video"
                        contentId={video.id}
                        initialLikeCount={getLikeData('video', video.id).likeCount}
                        initialIsLiked={getLikeData('video', video.id).isLiked}
                        userId={batchUserId}
                        onLikeChange={(isLiked, delta) => updateLikeData('video', video.id, isLiked, delta)}
                        className="h-8"
                      />
                    )
                  )}
                </div>
                <div className="flex items-center justify-between mt-2">
                  <span className="text-xs text-muted-foreground">
                    {video.is_public && video.creator_username && (
                      <span className="font-medium text-foreground">{video.creator_username} • </span>
                    )}
                    {new Date(video.created_at).toLocaleDateString()}
                  </span>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
      
      {/* Show More Button */}
      {hasMore && videos.length > 0 && (
        <div className="flex justify-center">
          <Button
            onClick={loadMoreVideos}
            disabled={loadingMore}
            variant="outline"
            className="px-8"
          >
            {loadingMore ? "Loading..." : "Show More"}
          </Button>
        </div>
      )}

      {/* Lightbox for full-size video viewing */}
      {lightboxVideo && lightboxVideo.downloads?.video_url && (
        <MediaLightbox
          type="video"
          url={lightboxVideo.downloads.video_url}
          prompt={lightboxVideo.prompt}
          open={!!lightboxVideo}
          onOpenChange={(open) => !open && setLightboxVideo(null)}
        />
      )}

      {/* Bulk action bar */}
      {selectionMode && (
        <GallerySelectionBar
          selectedCount={selectedItems.size}
          totalCount={videos.filter(v => v.status === 'completed' && v.downloads?.video_url).length}
          onSelectAll={selectAll}
          onDeselectAll={deselectAll}
          onCancel={cancelSelection}
          onDelete={bulkDelete}
          onDownload={bulkDownload}
          isDeleting={isBulkDeleting}
          isDownloading={isBulkDownloading}
        />
      )}

      {/* Single delete confirmation dialog */}
      <DeleteConfirmDialog
        open={!!deleteConfirmVideo}
        onOpenChange={(open) => !open && setDeleteConfirmVideo(null)}
        onConfirm={confirmSingleDelete}
        title="Delete this video?"
        description="This action cannot be undone."
        isDeleting={isDeleting}
      />

      {/* Bulk delete confirmation dialog */}
      <DeleteConfirmDialog
        open={showBulkDeleteConfirm}
        onOpenChange={setShowBulkDeleteConfirm}
        onConfirm={confirmBulkDelete}
        title={`Delete ${selectedCount} video${selectedCount > 1 ? 's' : ''}?`}
        description="This action cannot be undone."
        isDeleting={isBulkDeleting}
      />
    </div>
  )
}